import { Trash2, X } from 'lucide-react';
import { RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { IndexPath } from '@/Config/app/router/index.path';
import { MessageFeedback, MessageFeedbackValue } from '@/Domain/api/Conversation/dto/Message.dto';
import { Avatar, AvatarFallback, AvatarImage } from '@/Domain/components/Shared/Avatar/Avatar';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Checkbox } from '@/Domain/components/Shared/CheckBox/Checkbox';
import { Popover, PopoverAnchor, PopoverContent } from '@/Domain/components/Shared/Popover/Popover';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import { BookTextIcon } from '@/Domain/components/icons/BookTextIcon';
import { FingerprintIcon } from '@/Domain/components/icons/FingerprintIcon';
import { ZapIcon } from '@/Domain/components/icons/ZapIcon';

type FeedbackOption = {
  id: string;
  labelKey: string;
};

const NEGATIVE_OPTIONS: FeedbackOption[] = [
  { id: 'incorrect_info', labelKey: 'message_feedback.options.negative.incorrect_info' },
  { id: 'misunderstood', labelKey: 'message_feedback.options.negative.misunderstood' },
  { id: 'other', labelKey: 'message_feedback.options.other' },
];

const POSITIVE_OPTIONS: FeedbackOption[] = [
  { id: 'accurate_answer', labelKey: 'message_feedback.options.positive.accurate_answer' },
  { id: 'good_tone', labelKey: 'message_feedback.options.positive.good_tone' },
  { id: 'other', labelKey: 'message_feedback.options.other' },
];

const getAuthorDisplayName = (author: MessageFeedback['author']): string => {
  const firstName = author.first_name?.trim();
  const lastName = author.last_name?.trim();

  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(' ');
  }

  return author.email || '';
};

const getAuthorInitials = (author: MessageFeedback['author']): string => {
  const firstName = author.first_name?.trim();
  const lastName = author.last_name?.trim();

  if (firstName || lastName) {
    return [firstName?.[0], lastName?.[0]].filter(Boolean).join('').toUpperCase();
  }

  return author.email?.[0]?.toUpperCase() || '?';
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feedbackType: MessageFeedbackValue;
  existingFeedback: MessageFeedback | null;
  anchorRef: RefObject<HTMLDivElement | null>;
  nextStepsContext?: 'simulation';
  isSubmitting: boolean;
  isDeleting: boolean;
  onSubmit: (comment: string | null) => void;
  onDelete: () => void;
};

export const MessageFeedbackPopover = ({
  open,
  onOpenChange,
  feedbackType,
  existingFeedback,
  anchorRef,
  nextStepsContext,
  isSubmitting,
  isDeleting,
  onSubmit,
  onDelete,
}: Props) => {
  const { t } = useTranslation('inbox_v2');
  const { scenario = '' } = useParams<{ scenario: string }>();
  const showSimulationNextSteps = nextStepsContext === 'simulation' && feedbackType === 'negative';

  const options = feedbackType === 'positive' ? POSITIVE_OPTIONS : NEGATIVE_OPTIONS;

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [comment, setComment] = useState('');

  // Seed only on open or a thumb switch: a refreshed existingFeedback must not wipe what is being typed.
  useEffect(() => {
    if (!open) return;

    // If switching feedback type (e.g., positive to negative), reset the form
    // since the existing feedback options won't match the new type's options
    const isSwitchingFeedbackType = existingFeedback && existingFeedback.value !== feedbackType;

    if (isSwitchingFeedbackType || !existingFeedback) {
      // Reset state when opening for new feedback or switching types
      setSelectedOptions([]);
      setComment('');
      return;
    }

    // Parse existing feedback of the same type
    if (existingFeedback.comment) {
      const lines = existingFeedback.comment.split('\n');
      const matchedOptionIds: string[] = [];
      const unmatchedLines: string[] = [];

      lines.forEach((line) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;

        // Check if this line matches any option label
        const matchedOption = options.find((opt) => t(opt.labelKey) === trimmedLine);

        if (matchedOption) {
          matchedOptionIds.push(matchedOption.id);
        } else {
          unmatchedLines.push(trimmedLine);
        }
      });

      setSelectedOptions(matchedOptionIds);
      setComment(unmatchedLines.join('\n'));
    } else {
      setSelectedOptions([]);
      setComment('');
    }
  }, [open, feedbackType]);

  const handleOptionToggle = (optionId: string) => {
    setSelectedOptions((prev) =>
      prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId],
    );
  };

  const handleSubmit = () => {
    const selectedLabels = selectedOptions.map((optionId) => {
      const option = options.find((opt) => opt.id === optionId);
      return option ? t(option.labelKey) : '';
    });

    const combinedComment = [
      ...selectedLabels.filter(Boolean),
      comment.trim() ? comment.trim() : '',
    ]
      .filter(Boolean)
      .join('\n');

    onSubmit(combinedComment || null);
  };

  const isOtherSelected = selectedOptions.includes('other');
  const canSubmit = selectedOptions.length > 0 || comment.trim().length > 0;
  const isLoading = isSubmitting || isDeleting;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor virtualRef={anchorRef} />
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={8}
        className="w-80 p-0 rounded-2xl border border-border shadow-lg bg-card"
        data-testid="message-feedback-popover"
      >
        <div className="flex flex-col" data-testid="message-feedback-form">
          {/* Header with title and action buttons */}
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <h3
              className="text-sm font-medium text-foreground"
              data-testid="message-feedback-title"
            >
              {feedbackType === 'positive'
                ? t('message_feedback.popover.title_positive')
                : t('message_feedback.popover.title_negative')}
            </h3>
            <div className="flex items-center gap-1">
              <IconButton
                variant="custom"
                size="xs"
                className="text-foreground-subtle hover:text-red-500 border-transparent"
                onClick={onDelete}
                disabled={isLoading}
                data-testid="message-feedback-delete"
              >
                <Trash2 className="size-4" />
              </IconButton>
              <IconButton
                variant="custom"
                size="xs"
                className="text-foreground-subtle hover:text-muted-foreground border-transparent"
                onClick={() => onOpenChange(false)}
                data-testid="message-feedback-close"
              >
                <X className="size-4" />
              </IconButton>
            </div>
          </div>

          {/* Options */}
          <div className="flex flex-col gap-2 px-4 pb-3" data-testid="message-feedback-options">
            {options.map((option) => {
              const isSelected = selectedOptions.includes(option.id);
              return (
                <label
                  key={option.id}
                  className="flex items-center gap-2 cursor-pointer"
                  data-testid={`message-feedback-option-${option.id}`}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleOptionToggle(option.id)}
                  />
                  <span className="text-sm text-muted-foreground">{t(option.labelKey)}</span>
                </label>
              );
            })}
          </div>

          {/* Comment textarea */}
          <div className="px-4 pb-3">
            <AutosizeTextarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={
                isOtherSelected
                  ? feedbackType === 'positive'
                    ? t('message_feedback.popover.comment_placeholder_positive')
                    : t('message_feedback.popover.comment_placeholder_negative')
                  : t('message_feedback.popover.comment_placeholder_optional')
              }
              minHeight={30}
              maxHeight={100}
              className="!py-1.5 !px-3 text-sm border-border"
              data-testid="message-feedback-comment"
            />
          </div>

          {/* Submit button */}
          <div className="px-4 pb-3">
            <Button
              onClick={handleSubmit}
              disabled={isLoading || !canSubmit}
              className="w-full"
              size="sm"
              data-testid="message-feedback-submit"
            >
              {isSubmitting
                ? t('message_feedback.popover.submitting')
                : t('message_feedback.popover.submit')}
            </Button>
          </div>

          {showSimulationNextSteps && (
            <div
              className="border-t border-border px-4 pt-3 pb-3"
              data-testid="message-feedback-next-steps"
            >
              <p className="text-xs font-medium text-foreground mb-2">
                {t('message_feedback.popover.next_steps.title')}
              </p>
              <ul className="space-y-1.5 text-xs">
                <li>
                  <Link
                    to={`/i/${scenario}/${IndexPath.identity}`}
                    className="inline-flex items-center gap-2 text-info hover:underline"
                    data-testid="message-feedback-next-steps-identity"
                  >
                    <FingerprintIcon size={14} />
                    <span>{t('message_feedback.popover.next_steps.identity')}</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to={`/i/${scenario}/${IndexPath.knowledgeBase.root}`}
                    className="inline-flex items-center gap-2 text-info hover:underline"
                    data-testid="message-feedback-next-steps-kb"
                  >
                    <BookTextIcon size={14} />
                    <span>{t('message_feedback.popover.next_steps.knowledge_base')}</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to={`/i/${scenario}/${IndexPath.actions}`}
                    className="inline-flex items-center gap-2 text-info hover:underline"
                    data-testid="message-feedback-next-steps-actions"
                  >
                    <ZapIcon size={14} />
                    <span>{t('message_feedback.popover.next_steps.actions')}</span>
                  </Link>
                </li>
              </ul>
              <p className="mt-2 text-xs text-muted-foreground">
                {t('message_feedback.popover.next_steps.then_rerun')}
              </p>
            </div>
          )}

          {/* Footer with author info */}
          {existingFeedback && (
            <div
              className="flex items-center gap-2 px-4 pb-3 pt-3 border-t border-border"
              data-testid="message-feedback-author"
            >
              <Avatar className="h-4 w-4">
                {existingFeedback.author.avatar_url && (
                  <AvatarImage
                    src={existingFeedback.author.avatar_url}
                    alt={getAuthorDisplayName(existingFeedback.author)}
                  />
                )}
                <AvatarFallback className="text-xs bg-muted text-foreground-subtle">
                  {getAuthorInitials(existingFeedback.author)}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs text-foreground-subtle truncate">
                {getAuthorDisplayName(existingFeedback.author)}
              </span>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
